import { useState } from "react";
import {
  Check,
  Clock3,
  Inbox,
  LoaderCircle,
  Send,
  Sparkles,
  X
} from "lucide-react";
import type {
  CandidatePlannerSession,
  GuidanceInboxView,
  GuidanceItemView
} from "@roboscientist/contracts";

const itemTitle = (item: GuidanceItemView): string => {
  const candidate = item.payload.candidate;
  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    const title = (candidate as Record<string, unknown>).title;
    if (typeof title === "string" && title.trim()) return title;
  }
  return item.kind.replaceAll("_", " ");
};

const deliveryLabel = (item: GuidanceItemView): string => {
  const attempt = item.deliveryAttempts.at(-1);
  if (!attempt) return "awaiting delivery";
  return `${attempt.method} · ${attempt.status}`;
};

export function CandidateStudio({
  session,
  loading,
  error,
  selectedCandidateId,
  onSelectCandidate,
  onDiscuss,
  onAccept,
  onReject,
  onClose,
  mutating
}: {
  session: CandidatePlannerSession | null;
  loading: boolean;
  error: string | null;
  selectedCandidateId: string | null;
  onSelectCandidate: (candidateId: string) => void;
  onDiscuss: (message: string) => void;
  onAccept: (candidateId: string) => void;
  onReject: (candidateId: string) => void;
  onClose: () => void;
  mutating: boolean;
}) {
  const [message, setMessage] = useState("");
  const submit = () => {
    const value = message.trim();
    if (!value) return;
    onDiscuss(value);
    setMessage("");
  };
  return <aside className="lineage-inspector candidate-studio" aria-label="Candidate Studio">
    <header>
      <div className="candidate-studio__title"><span className="kicker">EPHEMERAL · READ ONLY</span><h3><Sparkles size={15} /> Candidate Studio</h3></div>
      <button type="button" className="icon-button" onClick={onClose} aria-label="Close Candidate Studio"><X size={15} /></button>
    </header>
    {loading || session?.state === "running" ? <div className="candidate-studio__status"><LoaderCircle size={16} className="spin" /><strong>Planner is reasoning from the latest completed Main head…</strong><span>No experiment, lineage, or Main turn is being changed.</span></div> : null}
    {error || session?.error ? <div className="candidate-studio__error"><strong>Planner needs attention</strong><span>{error ?? session?.error}</span></div> : null}
    {session?.summary ? <section><h4>Planner synthesis</h4><p>{session.summary}</p><small>Lineage revision · {Object.entries(session.lineageRevision).map(([id, revision]) => `${id}@${revision}`).join(" · ") || "empty"}</small></section> : null}
    {session?.candidates.map((candidate, index) => <article
      key={candidate.id}
      className={`candidate-card is-${candidate.state} ${selectedCandidateId === candidate.id ? "is-selected" : ""}`}
      onClick={() => onSelectCandidate(candidate.id)}
    >
      <header><span>GHOST {index + 1}</span><code>{candidate.directionPath.join(" / ")}</code></header>
      <h4>{candidate.title}</h4>
      <dl><div><dt>Hypothesis</dt><dd>{candidate.hypothesis}</dd></div><div><dt>Intervention</dt><dd>{candidate.intervention}</dd></div><div><dt>Decision rule</dt><dd>{candidate.decisionRule}</dd></div></dl>
      <p>{candidate.rationale}</p>
      <footer>
        {candidate.state === "draft" ? <>
          <button type="button" className="button button--primary" disabled={mutating} onClick={(event) => { event.stopPropagation(); onAccept(candidate.id); }}><Check size={12} /> Accept to Inbox</button>
          <button type="button" className="button" disabled={mutating} onClick={(event) => { event.stopPropagation(); onReject(candidate.id); }}><X size={12} /> Reject</button>
        </> : <span className={`candidate-card__state is-${candidate.state}`}>{candidate.state === "accepted" ? "Accepted · awaiting formal lineage" : "Rejected draft"}</span>}
      </footer>
    </article>)}
    {session && session.state !== "ended" ? <section className="candidate-discussion"><h4>Continue discussion</h4><textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Ask the planner to tighten a control, revise a rule, or compare alternatives…" disabled={session.state === "running" || mutating} /><button type="button" className="button" disabled={!message.trim() || session.state === "running" || mutating} onClick={submit}><Send size={12} /> Revise candidates</button></section> : null}
  </aside>;
}

export function GuidanceInboxInspector({
  inbox,
  loading,
  error,
  onApplyNow,
  onReject,
  onClose,
  mutating
}: {
  inbox: GuidanceInboxView | null;
  loading: boolean;
  error: string | null;
  onApplyNow: (itemId: string) => void;
  onReject: (itemId: string) => void;
  onClose: () => void;
  mutating: boolean;
}) {
  return <aside className="lineage-inspector guidance-inbox" aria-label="Agent Guidance Inbox">
    <header>
      <div><span className="kicker">DURABLE · PROPOSAL SCOPED</span><h3><Inbox size={15} /> Agent Inbox</h3></div>
      <button type="button" className="icon-button" onClick={onClose} aria-label="Close Agent Inbox"><X size={15} /></button>
    </header>
    <section className="guidance-inbox__summary"><div><strong>{inbox?.pendingCount ?? 0}</strong><span>pending</span></div><div><strong>{inbox?.revision ?? 0}</strong><span>revision</span></div><div><strong>{inbox?.lastAcknowledgedRevision ?? 0}</strong><span>acknowledged through</span></div></section>
    {loading ? <div className="candidate-studio__status"><LoaderCircle size={16} className="spin" /><span>Reconciling delivery and lineage receipts…</span></div> : null}
    {error ? <div className="candidate-studio__error"><span>{error}</span></div> : null}
    <div className="guidance-inbox__items">{inbox?.items.map((item) => {
      const terminal = item.state === "applied" || item.state === "rejected" || item.state === "expired";
      return <article key={item.id} className={`guidance-item is-${item.state}`}>
        <header><span>{item.priority.replace("_", " ")}</span><code>r{item.inboxRevision}</code></header>
        <h4>{itemTitle(item)}</h4>
        <p>{item.kind.replaceAll("_", " ")} · action <code>{item.actionId.slice(0, 14)}…</code></p>
        <div className="guidance-item__audit"><span><Clock3 size={10} /> {deliveryLabel(item)}</span><span>action · {item.action.state}</span></div>
        {!terminal ? <footer><button type="button" className="button button--primary" disabled={mutating} onClick={() => onApplyNow(item.id)}>Apply now</button><button type="button" className="button" disabled={mutating} onClick={() => onReject(item.id)}>Reject</button></footer> : <span className={`guidance-item__terminal is-${item.state}`}>{item.state}</span>}
      </article>;
    })}</div>
    {!loading && !inbox?.items.length ? <div className="lineage-inspector--empty guidance-inbox__empty"><Inbox size={20} /><strong>Inbox is empty</strong><p>Accepted candidates and human guidance will appear here with delivery and receipt state.</p></div> : null}
  </aside>;
}
